import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  CampaignHealthDto,
  CampaignsDashboardResponseDto,
  OperationalCardsDto,
  OperationalViewFilters,
  ProfileDetailResponseDto,
  ProfileMatrixResponseDto,
  SupplierDetailResponseDto,
  SupplierListResponseDto,
} from '@/api/operational-view-client';
import { useOperationalViewClient } from '@/api/operational-view-client-context';

// =============================================================================
// use-operational-view.ts — Hooks TanStack Query du read-model A-15 ADMIN (S14).
// =============================================================================
// 3 lectures (agrégats / drill-down / santé) en useQuery — aucune mutation (admin LIT, jamais
// d'écriture, invariant n°1). Le client effectif vient du OperationalViewClientProvider (doublable,
// stub-first). ZÉRO règle métier ici : l'autorité (RBAC AdminSessionGuard, withAdmin, calcul au vol,
// flags/couleurs, alertes, santé) est l'API. Les filtres (F) entrent dans la queryKey pour un
// refetch automatique au changement (cycle/profil/séniorité).

const OPERATIONAL_VIEW_KEY = ['operational-view'] as const;

/** Sérialise les filtres en clé stable (ordre déterministe) pour la queryKey. */
function filtersKey(filters: OperationalViewFilters): readonly unknown[] {
  return [
    filters.cycleNumber ?? null,
    filters.profileCode ?? null,
    filters.seniorityLevel ?? null,
    filters.supplierSiret ?? null,
  ];
}

// =============================================================================
// S14-bis §2.5 v2.2 — hooks des pages Profils & Fournisseurs (5 lectures, aucune mutation).
// L'ancien read-model A-15 (agrégats 4-couleurs / drill-down / santé) a été RETIRÉ (refonte §2.5 v2.2).
// =============================================================================

/** Clé de query générique §2.5 v2.2 (segment + campagne + filtres F). */
function v22Key(
  segment: string,
  campaignId: string,
  filters: OperationalViewFilters,
): readonly unknown[] {
  return [...OPERATIONAL_VIEW_KEY, segment, campaignId, ...filtersKey(filters)];
}

/** Clé de query du tableau de bord macroscopique V-DASH (vue globale, sans campagne ni filtres). */
export const campaignsDashboardKey = [
  ...OPERATIONAL_VIEW_KEY,
  'campaigns-dashboard',
] as const;

/**
 * (V-DASH — Tableau de bord macroscopique A-02) Synthèse + registre enrichi + criticité, UN round-trip
 * (PAS de fan-out N+1). Aucune règle métier : l'API compose synthèse/volumétrie/criticité AU VOL.
 */
export function useCampaignsDashboard(): UseQueryResult<
  CampaignsDashboardResponseDto,
  Error
> {
  const client = useOperationalViewClient();
  return useQuery<CampaignsDashboardResponseDto, Error>({
    queryKey: campaignsDashboardKey,
    queryFn: () => client.getCampaignsDashboard(),
  });
}

/** Intervalle de polling par défaut du suivi « temps réel » A4 (30 s) — TanStack Query, PAS de WebSocket. */
const HEALTH_REFETCH_INTERVAL_MS = 30_000;

/**
 * (§2.5 A4 — Suivi temps réel) Santé/alertes de pilotage. Le « temps réel » est un POLLING léger
 * (`refetchInterval`, TanStack Query) — PAS de WebSocket ni d'infra realtime. Aucune règle métier :
 * l'API calcule participation/Hors Jeux/alertes AU VOL (autorité). L'ancien read-model A-15
 * (agrégats 4-couleurs / drill-down) a été RETIRÉ ; la santé D+E reste exposée et est ici recâblée.
 */
export function useHealth(
  campaignId: string,
  filters: OperationalViewFilters,
  refetchIntervalMs: number = HEALTH_REFETCH_INTERVAL_MS,
): UseQueryResult<CampaignHealthDto, Error> {
  const client = useOperationalViewClient();
  return useQuery<CampaignHealthDto, Error>({
    queryKey: v22Key('health', campaignId, filters),
    queryFn: () => client.getHealth(campaignId, filters),
    refetchInterval: refetchIntervalMs,
  });
}

/** (§2.5 v2.2 — Section 1) Cards communes Profils & Fournisseurs. */
export function useCards(
  campaignId: string,
  filters: OperationalViewFilters,
): UseQueryResult<OperationalCardsDto, Error> {
  const client = useOperationalViewClient();
  return useQuery<OperationalCardsDto, Error>({
    queryKey: v22Key('cards', campaignId, filters),
    queryFn: () => client.getCards(campaignId, filters),
  });
}

/** (§2.5 v2.2 — Page Profils §2) Matrice Profil × Niveau. */
export function useProfileMatrix(
  campaignId: string,
  filters: OperationalViewFilters,
): UseQueryResult<ProfileMatrixResponseDto, Error> {
  const client = useOperationalViewClient();
  return useQuery<ProfileMatrixResponseDto, Error>({
    queryKey: v22Key('profile-matrix', campaignId, filters),
    queryFn: () => client.getProfileMatrix(campaignId, filters),
  });
}

/** (§2.5 v2.2 — Page Fournisseurs §2) Liste fournisseurs. */
export function useSupplierList(
  campaignId: string,
  filters: OperationalViewFilters,
): UseQueryResult<SupplierListResponseDto, Error> {
  const client = useOperationalViewClient();
  return useQuery<SupplierListResponseDto, Error>({
    queryKey: v22Key('supplier-list', campaignId, filters),
    queryFn: () => client.getSupplierList(campaignId, filters),
  });
}

/** (§2.5 v2.2 — Page Profils §3) Détail d'un couple ; chargé à la demande (`enabled`). */
export function useProfileDetail(
  campaignId: string,
  filters: OperationalViewFilters,
  enabled: boolean,
): UseQueryResult<ProfileDetailResponseDto, Error> {
  const client = useOperationalViewClient();
  return useQuery<ProfileDetailResponseDto, Error>({
    queryKey: v22Key('profile-detail', campaignId, filters),
    queryFn: () => client.getProfileDetail(campaignId, filters),
    enabled,
  });
}

/** (§2.5 v2.2 — Page Fournisseurs §3) Détail d'un fournisseur ; chargé à la demande (`enabled`). */
export function useSupplierDetail(
  campaignId: string,
  filters: OperationalViewFilters,
  enabled: boolean,
): UseQueryResult<SupplierDetailResponseDto, Error> {
  const client = useOperationalViewClient();
  return useQuery<SupplierDetailResponseDto, Error>({
    queryKey: v22Key('supplier-detail', campaignId, filters),
    queryFn: () => client.getSupplierDetail(campaignId, filters),
    enabled,
  });
}
